import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Maximize2, Minimize2, RefreshCw, Car, LogIn, LogOut, CalendarClock, Clock3, AlertTriangle, Settings, WifiOff, CheckCircle2 } from "lucide-react";

const STAGE_COLOR = ["#3B82F6", "#F59E0B", "#F97316", "#8B5CF6", "#22D3EE", "#A3E635", "#EAB308", "#10B981"];
const LEVEL = { green: "text-emerald-300", yellow: "text-amber-300", orange: "text-orange-300", red: "text-rose-300", blue: "text-blue-300" };
const r0 = (n) => (n == null ? "—" : n);

const Kpi = ({ icon: Icon, label, value, tone }) => (
  <div className="flex min-w-0 flex-col justify-between rounded-xl border border-white/8 bg-white/[0.03] p-2.5">
    <div className="flex items-center gap-1.5"><Icon size={13} className={tone} /><span className="truncate text-[9px] uppercase tracking-wider text-[#7E91AC]">{label}</span></div>
    <div className="mt-1 text-2xl font-extrabold text-white xl:text-3xl">{ro(value)}</div>
  </div>
);
const ro = (v) => (v == null ? "—" : v);

const Panel = ({ title, children, right }) => (
  <div className="flex min-h-0 min-w-0 flex-col rounded-xl border border-white/8 bg-[#0a1628]/60">
    <div className="flex shrink-0 items-center justify-between border-b border-white/8 px-3 py-1.5"><h3 className="font-heading text-[11px] font-bold uppercase tracking-wider text-white">{title}</h3>{right}</div>
    <div className="min-h-0 flex-1 overflow-hidden p-2.5">{children}</div>
  </div>
);

export default function WarRoom() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin" || user?.role === "root" || user?.role_name === "Owner";
  const rootRef = useRef(null);
  const [d, setD] = useState(null);
  const [updated, setUpdated] = useState(null);
  const [offline, setOffline] = useState(false);
  const [tv, setTv] = useState(false);
  const [now, setNow] = useState(new Date());
  const [showCfg, setShowCfg] = useState(false);
  const [tgt, setTgt] = useState({ unit_out_per_day: 5, ready_per_day: 5, panel_per_day: 20 });

  const load = useCallback(() => {
    api.get("/war-room").then((r) => { setD(r.data); setUpdated(new Date()); setOffline(false); }).catch(() => setOffline(true));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { api.get("/war-room/targets").then((r) => setTgt(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const h = () => setTv(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h); return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  const toggleTv = async () => {
    try {
      if (!document.fullscreenElement) await rootRef.current?.requestFullscreen();
      else await document.exitFullscreen();
    } catch (e) { setTv((v) => !v); }
  };
  const saveTargets = async () => { try { const r = await api.put("/war-room/targets", tgt); setTgt(r.data); setShowCfg(false); load(); } catch (e) { /* ignore */ } };

  const wibTime = now.toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const wibDate = now.toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const k = d?.kpi || {};
  const maxPipe = Math.max(1, ...((d?.pipeline || []).map((p) => p.count)));

  return (
    <div ref={rootRef} data-testid="war-room-page" className={`ti-dark flex flex-col overflow-hidden bg-[#050B14] ${tv ? "h-screen w-screen p-3" : "-m-3 min-h-[calc(100dvh-3rem)] w-full max-w-full p-3 sm:-m-5 sm:p-4 lg:-mb-5"}`}>
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-2 pb-2">
        <div className="flex min-w-0 items-center gap-2">
          <img src="/titanium-logo.png" alt="TITANIUM" className="h-6 w-auto object-contain" />
          <div className="min-w-0"><h1 className="font-heading text-sm font-extrabold tracking-tight text-white sm:text-base">WAR ROOM</h1><p className="truncate text-[9px] text-[#7E91AC]">Operations Control · read-only</p></div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right"><div className="font-mono text-lg font-extrabold leading-none text-white sm:text-xl" data-testid="wr-clock">{wibTime}</div><div className="text-[9px] text-[#AAB8CC]">{wibDate} WIB</div></div>
          {offline ? <span className="flex items-center gap-1 rounded-md bg-rose-500/15 px-2 py-1 text-[9px] font-semibold text-rose-300"><WifiOff size={11} />Data terakhir {updated ? updated.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : "—"}</span>
            : <span className="hidden text-[9px] text-[#7E91AC] sm:inline">Update {updated ? updated.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : "—"}</span>}
          {isOwner && !tv && <button data-testid="wr-cfg" onClick={() => setShowCfg((s) => !s)} className="rounded-lg border border-[#29466D] bg-[#102544] p-1.5 text-[#C3D0E2] hover:text-white"><Settings size={14} /></button>}
          {!tv && <button data-testid="wr-refresh" onClick={load} className="rounded-lg border border-[#29466D] bg-[#102544] p-1.5 text-[#C3D0E2] hover:text-white"><RefreshCw size={14} /></button>}
          <button data-testid="wr-tv" onClick={toggleTv} className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-blue-500">{tv ? <><Minimize2 size={13} />Keluar</> : <><Maximize2 size={13} />Mode TV</>}</button>
        </div>
      </div>

      {showCfg && (
        <div className="mb-2 grid shrink-0 grid-cols-1 gap-2 rounded-xl border border-blue-400/30 bg-blue-500/5 p-2.5 sm:grid-cols-4" data-testid="wr-cfg-panel">
          {["unit_out_per_day", "ready_per_day", "panel_per_day"].map((key) => (
            <label key={key} className="text-[10px] text-[#AAB8CC]">{key === "unit_out_per_day" ? "Target Unit Keluar/hari" : key === "ready_per_day" ? "Target Ready/hari" : "Target Panel/hari"}
              <input type="number" value={tgt[key]} onChange={(e) => setTgt({ ...tgt, [key]: Number(e.target.value) })} className="mt-1 h-8 w-full rounded border border-[#29466D] bg-[#0a2038] px-2 text-sm text-white" /></label>
          ))}
          <button data-testid="wr-cfg-save" onClick={saveTargets} className="mt-auto h-8 rounded bg-blue-600 text-[11px] font-bold text-white">Simpan Target</button>
        </div>
      )}

      {/* KPI row */}
      <div className="grid shrink-0 grid-cols-3 gap-2 lg:grid-cols-6">
        <Kpi icon={Car} label="Total di Bengkel" value={k.total_in_workshop} tone="text-amber-300" />
        <Kpi icon={LogIn} label="Unit Masuk (hari ini)" value={k.unit_in_today} tone="text-emerald-300" />
        <Kpi icon={LogOut} label="Unit Keluar (hari ini)" value={k.unit_out_today} tone="text-blue-300" />
        <Kpi icon={CalendarClock} label="Ready / Siap Keluar" value={k.ready_units} tone="text-cyan-300" />
        <Kpi icon={Clock3} label="Idle >24 Jam" value={k.idle_24h} tone="text-orange-300" />
        <Kpi icon={AlertTriangle} label=">14 Hari" value={k.overdue} tone="text-rose-300" />
      </div>

      {/* Middle: pipeline | schedule | aging */}
      <div className="mt-2 grid min-h-0 flex-1 grid-cols-1 gap-2 lg:grid-cols-3">
        <Panel title="Pipeline Produksi">
          <div className="flex h-full flex-col justify-center gap-1.5">
            {(d?.pipeline || []).map((p, i) => (
              <div key={p.stage} className="flex items-center gap-2" data-testid="wr-pipe-row">
                <span className="w-20 shrink-0 truncate text-[10px] text-[#C3D0E2]">{p.stage}</span>
                <div className="h-4 flex-1 overflow-hidden rounded bg-white/5"><div className="h-full rounded" style={{ width: `${(p.count / maxPipe) * 100}%`, background: STAGE_COLOR[i % STAGE_COLOR.length], minWidth: p.count ? 8 : 0 }} /></div>
                <span className="w-6 shrink-0 text-right text-xs font-bold text-white">{p.count}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Jadwal / Siap Keluar" right={<span className="text-[9px] text-[#7E91AC]">{(d?.schedule || []).length} unit</span>}>
          <div className="flex h-full min-h-0 flex-col gap-1 overflow-y-auto pr-1">
            {(d?.schedule || []).length === 0 ? <p className="py-6 text-center text-[11px] text-[#7E91AC]">Belum ada unit siap keluar.</p> : (d?.schedule || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg border border-white/8 bg-white/5 px-2 py-1.5" data-testid="wr-sched-row">
                <span className="font-mono text-xs font-bold text-white">{s.license_plate}</span>
                <div className="min-w-0 flex-1"><div className="truncate text-[10px] text-[#C3D0E2]">{s.vehicle} · WO {s.wo_number}</div><div className="text-[9px] text-[#7E91AC]">{s.pic} · {s.days_in != null ? `${s.days_in} hr` : "-"}</div></div>
                {s.ready ? <span className="flex items-center gap-0.5 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold text-emerald-300"><CheckCircle2 size={10} />READY</span> : <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold text-amber-300">{s.stage}</span>}
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Aging Unit (sejak masuk)">
          <div className="grid h-full grid-cols-2 gap-2">
            {[["0-3", "bg-emerald-500/10 border-emerald-400/25", "text-emerald-300"], ["4-7", "bg-amber-500/10 border-amber-400/25", "text-amber-300"], ["8-14", "bg-orange-500/10 border-orange-400/25", "text-orange-300"], [">14", "bg-rose-500/10 border-rose-400/25", "text-rose-300"]].map(([key, box, txt]) => (
              <div key={key} className={`flex flex-col items-center justify-center rounded-lg border ${box}`} data-testid="wr-aging">
                <span className={`text-3xl font-extrabold ${txt}`}>{r0(d?.aging?.[key])}</span>
                <span className="text-[10px] text-[#AAB8CC]">{key} hari</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Bottom: targets + ticker */}
      <div className="mt-2 grid shrink-0 grid-cols-1 gap-2 lg:grid-cols-3">
        <div className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#0a1628]/60 px-3 py-2 lg:col-span-1">
          <TargetBar label="Unit Keluar" real={d?.targets?.unit_out?.real} target={d?.targets?.unit_out?.target} />
          <TargetBar label="Ready Unit" real={d?.targets?.ready?.real} target={d?.targets?.ready?.target} />
        </div>
        <div className="flex items-center gap-2 overflow-hidden rounded-xl border border-white/8 bg-[#0a1628]/60 px-3 py-2 lg:col-span-2" data-testid="wr-ticker">
          <AlertTriangle size={14} className="shrink-0 text-amber-300" />
          <div className="relative flex-1 overflow-hidden">
            {(d?.alerts || []).length === 0 ? <span className="text-[11px] text-emerald-300">Semua indikator normal.</span> :
              <div className="flex gap-6 whitespace-nowrap" style={{ animation: "wrticker 24s linear infinite" }}>
                {[...(d?.alerts || []), ...(d?.alerts || [])].map((a, i) => <span key={i} className={`text-[11px] font-semibold ${LEVEL[a.level] || "text-white"}`}>● {a.text}</span>)}
              </div>}
          </div>
        </div>
      </div>
      <style>{`@keyframes wrticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}`}</style>
    </div>
  );
}

const TargetBar = ({ label, real, target }) => {
  const pct = Math.min(100, target ? (Number(real || 0) / Number(target)) * 100 : 0);
  const tone = pct >= 100 ? "#10B981" : pct >= 60 ? "#F59E0B" : "#F43F5E";
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between text-[10px] text-[#AAB8CC]"><span className="truncate">{label}</span><span className="font-bold text-white">{ro(real)}/{ro(target)}</span></div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-white/8"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: tone }} /></div>
    </div>
  );
};
